import { ChevronLeft } from 'lucide-react';
import { useRegisterOverlay } from '@/stores/overlayStore';
import { IconButton } from '@/components/ui';
import { ONBOARDING_COPY } from './onboardingCopy';

export interface TroubleshootingForwardingProps {
  onBack: () => void;
}

/**
 * Full-screen troubleshooting screen shown from the onboarding Gmail
 * connect flow (Figma node 8351:75920, "Email monitoring /
 * Troubleshooting"). Replaces the normal OnboardingLayout (own header +
 * own back button) so we can hide the progress bar while the user is
 * reading the static Q&A items — there is no accordion/expand affordance
 * in the design, this is plain text.
 *
 * The item list itself is not owned here: it comes from
 * `ONBOARDING_COPY.troubleshooting`, which carries the design-approved set
 * (five items as of YAR-160 AC5). This component renders whatever is there,
 * so the count is deliberately not hardcoded anywhere in the markup.
 */
export function TroubleshootingForwarding({
  onBack,
}: TroubleshootingForwardingProps) {
  // A full-screen takeover, mounted only while shown: hide and pause the
  // bottom toast slot underneath it (YAR-476).
  useRegisterOverlay(true);
  const copy = ONBOARDING_COPY.troubleshooting;

  return (
    <div className="fixed inset-0 z-[200] bg-background flex flex-col overflow-y-auto">
      <header className="sticky top-0 z-[100] bg-background px-5 py-2">
        <IconButton
          onClick={onBack}
          aria-label="Go back"
          variant="ghost"
        >
          <ChevronLeft className="w-5 h-5 text-icon-default" />
        </IconButton>
      </header>

      <main className="flex-1 flex flex-col gap-10 px-5 pt-10 pb-[max(2.75rem,env(safe-area-inset-bottom))]">
        <h1 className="text-[28px] leading-8 font-bold text-text-hero">
          {copy.title}
        </h1>

        <div className="flex flex-col gap-4">
          {copy.items.map((item) => (
            <div
              key={item.q}
              className="flex flex-col gap-1"
            >
              {/* Figma `text/headings` #000 (QA on YAR-160). */}
              <p className="text-base font-medium text-text-headings leading-6">
                {item.q}
              </p>
              <p className="text-sm text-muted-foreground leading-5">
                {item.a}
              </p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
